


























































































import {
  defineComponent,
  nextTick,
  PropType,
  reactive,
  ref,
  watch
} from '@vue/composition-api';
import { createNamespacedHelpers, useStore } from 'vuex-composition-helpers';

import { useVModel } from '@vueuse/core';
import { delay } from 'lodash';

import { useWorkFlow } from '../workflow-canvas/composables';

import { IRootState } from '@/store';
import { IWorkflow, IWorkflowState, IWorkflowStateData } from '@/models';
import { ISystemError } from '@/models/error/error.types';
import { SelectedDataTypes } from './types';
import {
  IWorkflowStoreState,
  IWorkflowGetters,
  IWorkflowActions,
  IWorkflowMutations
} from '@/store/workflow';

import AlTooltipBtn from '@/components/common/AlTooltipBtn.vue';
import WorkflowStateDetailOptionButton from './WorkflowStateDetailOptionButton.vue';
import WorkflowTransitionForm from './WorkflowTransitionForm.vue';
import WorkflowStateForm from './WorkflowStateForm.vue';
import WorkflowSnackbar from './WorkflowSnackbar.vue';
import { useWorkFlowGraph } from '../workflow-canvas/composables/useWorkFlowGraph';

export const DRAWER_WIDTH = 450;

export default defineComponent({
  name: 'WorkflowStateDetailDrawer',

  components: {
    AlTooltipBtn,
    WorkflowSnackbar,
    WorkflowStateDetailOptionButton,
    WorkflowStateForm,
    WorkflowTransitionForm
  },

  emits: ['deleted-state'],

  props: {
    value: { type: Boolean },
    workflow: {
      type: Object as PropType<IWorkflow>,
      required: true
    },
    state: {
      type: Object as PropType<IWorkflowState | null>,
      default: () => null
    }
  },

  setup(props, { emit }) {
    const isActive = useVModel(props);

    const {
      updateNodeData,
      updateNode,
      updateEdgeData,
      disabled
    } = useWorkFlow();

    const { getNodeTypeFromState } = useWorkFlowGraph();

    const workflowStateData = ref<IWorkflowStateData | null>(props.state);

    watch(
      () => props.state,
      value => {
        workflowStateData.value = value;
      },
      {
        deep: true
      }
    );

    const error = ref<ISystemError | null>(null);

    function clearError() {
      error.value = null;
    }

    const isLoading = ref(false);

    function onStateDeletedHandler() {
      isActive.value = false;

      emit('deleted-state', props.state?.id?.toString());
    }

    function onStateUpdatedHandler(nodeId: string, nodeData: IWorkflowState) {
      updateNodeData(nodeId, nodeData);

      updateNode(nodeId, {
        type: getNodeTypeFromState(nodeData)
      });

      setSnackbar('Updated State', 'Form is automatically saved after updates');
    }

    const isSnackbarActive = ref(false);

    const snackbarData = reactive({
      title: '',
      description: ''
    });

    function setSnackbar(title: string, description = '') {
      snackbarData.title = title;
      snackbarData.description = description;

      nextTick(() => {
        isSnackbarActive.value = true;
      });
    }

    const store = useStore<IRootState>();

    const { useActions, useGetters } = createNamespacedHelpers<
      IWorkflowStoreState,
      IWorkflowGetters,
      IWorkflowActions,
      IWorkflowMutations
    >(store, 'workflow');

    const { getWorkflowStateById } = useGetters(['getWorkflowStateById']);

    const { updateWorkflowState } = useActions(['updateWorkflowState']);

    function toggleAllowEdit(workflowState: IWorkflowStateData) {
      isLoading.value = true;
      const payloadDelta: IWorkflowStateData = {
        id: workflowState.id,
        is_locked: !workflowState.is_locked
      };

      updateWorkflowState({
        workflowId: props.workflow.id!,
        state: payloadDelta
      })
        .then(() => {
          onStateUpdatedHandler(
            payloadDelta.id!.toString(),
            getWorkflowStateById.value(payloadDelta.id!)!
          );

          workflowStateData.value!.is_locked = payloadDelta.is_locked;
        })
        .finally(() => {
          isLoading.value = false;
        });
    }

    return {
      // REFS

      isActive,

      workflowStateData,
      isLoading,
      DRAWER_WIDTH,

      error,
      clearError,

      delay,

      requiredRule: [(v: string) => !!v || 'Field is required'],
      SelectedDataTypes,

      // workflow functions
      updateEdgeData,
      updateNodeData,
      onStateUpdatedHandler,
      onStateDeletedHandler,

      // snackbar
      isSnackbarActive,
      snackbarData,

      disabled,

      toggleAllowEdit
    };
  }
});
